import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  FileSignature,
  CheckCircle,
  Shield,
  AlertTriangle,
  Loader2,
  Briefcase,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MVA rate in Norway
const MVA_RATE = 0.25;

// Minimum budget for 30-day payment option
const MIN_BUDGET_FOR_30_DAYS = 250000;

// Project type labels
const PROJECT_TYPES = {
  assembly: "Montering",
  disassembly: "Demontering",
  modification: "Modifikasjon",
  inspection: "Inspeksjon",
  rental: "Utleie",
  other: "Annet"
};

export default function ClientContractSignV3() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [holdingProfile, setHoldingProfile] = useState(null);
  const [signing, setSigning] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [paymentTerms, setPaymentTerms] = useState(14);
  
  // Signature canvas ref
  const signaturePadRef = useRef(null);

  useEffect(() => {
    fetchProject();
    fetchProfiles();
  }, [projectId, token]);

  useEffect(() => {
    // Set signer email from user
    if (user?.email) {
      setSignerEmail(user.email);
    }
  }, [user]);

  // Clear signature
  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  // Check if signature is empty
  const isSignatureEmpty = () => {
    return signaturePadRef.current?.isEmpty() ?? true;
  };

  // Get signature as data URL
  const getSignatureDataUrl = () => {
    if (!signaturePadRef.current || isSignatureEmpty()) return null;
    return signaturePadRef.current.toDataURL('image/png');
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects-v3/browse/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(res.data);
      // Set initial payment terms from project if available
      if (res.data.payment_terms_days) {
        setPaymentTerms(res.data.payment_terms_days);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Kunne ikke laste prosjekt");
      navigate("/client/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const clientRes = await axios.get(`${API_URL}/api/client-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientProfile(clientRes.data);
    } catch (error) {
    }
    
    try {
      const holdingRes = await axios.get(`${API_URL}/api/holding/public-info`);
      setHoldingProfile(holdingRes.data);
    } catch (error) {
    }
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error(t('contracts.mustAgreeToTerms'));
      return;
    }
    if (!signerName.trim()) {
      toast.error(t('contracts.enterYourName'));
      return;
    }
    if (!signerEmail.trim()) {
      toast.error("Vennligst skriv inn e-post");
      return;
    }
    if (isSignatureEmpty()) {
      toast.error(t('contracts.pleaseSign') || "Vennligst tegn signaturen din");
      return;
    }

    const signatureImage = getSignatureDataUrl();
    const budget = project.final_budget || project.proposed_budget;

    setSigning(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects-v3/sign-contract/${projectId}`,
        { 
          signer_name: signerName,
          signer_email: signerEmail,
          signature_image: signatureImage,
          payment_terms_days: paymentTerms,
          // Send all contract details to be saved
          contract_details: {
            project_title: project.title,
            project_type: project.project_type,
            project_description: project.description,
            location_address: project.location_address,
            location_city: project.location_city,
            start_date: project.start_date,
            end_date: project.end_date,
            estimated_duration_days: project.estimated_duration_days,
            crew_size: project.crew_size,
            budget_excl_mva: budget,
            mva_amount: budget * MVA_RATE,
            budget_incl_mva: budget * (1 + MVA_RATE),
            payment_terms_days: paymentTerms,
            client_company: clientProfile?.company_name || project.client_company,
            client_org_number: clientProfile?.org_number || "",
            client_address: clientProfile?.address || "",
            client_email: signerEmail,
            holding_company: "Doserne Utleie AS",
            holding_org_number: holdingProfile?.org_number || "",
            holding_address: holdingProfile?.address || "",
            holding_email: holdingProfile?.contact_email || "",
            signer_name: signerName,
            signer_email: signerEmail,
            signature_image: signatureImage
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('contracts.contractSigned') + " " + t('contracts.projectNowLive'));
      navigate(`/client/projects-v3/${projectId}`);
    } catch (error) {
      console.error("Error signing contract:", error);
      toast.error(error.response?.data?.detail || "Could not sign contract");
    } finally {
      setSigning(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const getProjectTypeLabel = (type) => {
    return PROJECT_TYPES[type] || type || "Ikke spesifisert";
  };

  // Check if 30-day option is available
  const budget = project?.final_budget || project?.proposed_budget || 0;
  const can30Days = budget >= MIN_BUDGET_FOR_30_DAYS;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#23F7DD] animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
          <h2 className="text-xl text-white mb-2">{t('projects.noProjects')}</h2>
          <Button onClick={() => navigate("/client/projects")} variant="outline">
            {t('common.back')}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  if (project.status !== "approved_pending_client_sign") {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
          <h2 className="text-xl text-white mb-2">{t('contracts.projectNotReady')}</h2>
          <p className="text-gray-400 mb-4">{t('common.status')}: {project.status}</p>
          <Button onClick={() => navigate(`/client/projects-v3/${projectId}`)} variant="outline">
            {t('common.back')}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="contract-sign-v3">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">{t('contracts.signContract')}</h1>
            <p className="text-gray-400">{project.title}</p>
          </div>
        </div>

        {/* Contract Summary */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardHeader className="border-b border-[#2a2a4a]">
            <CardTitle className="text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.contractSummary')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Company Details - Client & Holding */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Client Company */}
              <div className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                <h4 className="text-sm text-gray-400 mb-3 uppercase tracking-wider">{t('contracts.clientCompany') || 'Oppdragsgiver'}</h4>
                <div className="space-y-2">
                  <p className="text-white font-semibold">{clientProfile?.company_name || project?.client_company || <span className="text-yellow-500 italic">{t('common.notFilled') || 'Ikke utfylt'}</span>}</p>
                  <p className="text-gray-400 text-sm">{clientProfile?.org_number ? `Org.nr: ${clientProfile.org_number}` : <span className="text-yellow-500/70 italic text-xs">{t('common.orgNumberMissing') || 'Org.nr mangler'}</span>}</p>
                  <p className="text-gray-400 text-sm">{clientProfile?.address || <span className="text-yellow-500/70 italic text-xs">{t('common.addressMissing') || 'Adresse mangler'}</span>}</p>
                  <p className="text-gray-400 text-sm">{clientProfile?.contact_email || user?.email}</p>
                </div>
              </div>
              
              {/* Holding Company */}
              <div className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                <h4 className="text-sm text-gray-400 mb-3 uppercase tracking-wider">{t('contracts.holdingCompany') || 'Leverandør'}</h4>
                <div className="space-y-2">
                  <p className="text-white font-semibold">Doserne Utleie AS</p>
                  <p className="text-gray-400 text-sm">{holdingProfile?.org_number ? `Org.nr: ${holdingProfile.org_number}` : 'Org.nr: 937 207 050'}</p>
                  <p className="text-gray-400 text-sm">{holdingProfile?.address || <span className="text-yellow-500/70 italic text-xs">{t('common.addressMissing') || 'Adresse mangler'}</span>}</p>
                  <p className="text-gray-400 text-sm">{holdingProfile?.contact_email || <span className="text-yellow-500/70 italic text-xs">{t('common.emailMissing') || 'E-post mangler'}</span>}</p>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.title')}</p>
                    <p className="text-white font-medium">{project.title}</p>
                  </div>
                </div>
                
                {/* Project Type - NEW */}
                <div className="flex items-start gap-3">
                  <Briefcase className="w-5 h-5 text-purple-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.projectType') || 'Type arbeid'}</p>
                    <p className="text-purple-400 font-semibold">{getProjectTypeLabel(project.project_type)}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.location')}</p>
                    <p className="text-white">{project.location_address}, {project.location_city}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.startDate')}</p>
                    <p className="text-white">{formatDate(project.start_date)}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.teamSize')}</p>
                    <p className="text-white">{project.crew_size} {t('projects.workers').toLowerCase()}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.duration')}</p>
                    <p className="text-white">{project.estimated_duration_days} {t('common.days')}</p>
                  </div>
                </div>
                
                {/* Total Cost with MVA breakdown */}
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-[#23F7DD] mt-1" />
                  <div>
                    <p className="text-gray-400 text-sm">{t('projects.totalCost')}</p>
                    <p className="text-[#23F7DD] font-bold text-xl">
                      {formatCurrency(budget * (1 + MVA_RATE))}
                    </p>
                    <div className="text-xs text-gray-500 mt-1">
                      <span>{formatCurrency(budget)} {t('contracts.exMva') || 'eks. mva'}</span>
                      <span className="mx-1">+</span>
                      <span>{formatCurrency(budget * MVA_RATE)} {t('contracts.mva') || 'mva'} (25%)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Description - NEW */}
            {project.description && (
              <div className="border-t border-[#2a2a4a] pt-4">
                <h4 className="text-gray-400 text-sm mb-2">{t('projects.description') || 'Beskrivelse'}</h4>
                <p className="text-white bg-[#0f0f1a] p-3 rounded-lg">{project.description}</p>
              </div>
            )}

            {/* Contract Terms */}
            <div className="border-t border-[#2a2a4a] pt-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                {t('contracts.contractTerms')}
              </h3>
              
              <div className="p-4 bg-[#0f0f1a] rounded-lg text-sm text-gray-300 space-y-3">
                <p>{t('contracts.contractTermsIntro')}</p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li>{t('contracts.termProjectDetails')}</li>
                  <li>{t('contracts.termPaymentSelected') || 'Betalingsbetingelser velges nedenfor'}</li>
                  <li>Doserne Utleie AS vil utføre arbeidet som beskrevet</li>
                  <li>{t('contracts.termChangesInWriting')}</li>
                  <li>{t('contracts.termLegallyBinding')}</li>
                </ul>
              </div>
            </div>

            {/* Payment Info */}
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-blue-400 text-sm font-medium mb-2">{t('contracts.paymentInfo')}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">{t('contracts.paymentDeadline')}</p>
                  <p className="text-white">{t('contracts.selectedBelow') || 'Velges nedenfor'}</p>
                </div>
                <div>
                  <p className="text-gray-400">{t('contracts.invoicedTo')}</p>
                  <p className="text-white">{clientProfile?.company_name || project.client_company || project.client_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardHeader className="border-b border-[#2a2a4a]">
            <CardTitle className="text-white flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-green-400" />
              {t('contracts.yourSignature')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Terms Agreement */}
            <div className="flex items-start gap-3">
              <Checkbox 
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                className="mt-1"
              />
              <Label htmlFor="terms" className="text-gray-300 cursor-pointer">
                {t('contracts.agreeToTerms')} {clientProfile?.company_name || project?.client_company || t('common.company')}.
              </Label>
            </div>

            {/* Signer Name */}
            <div className="space-y-2">
              <Label htmlFor="signerName" className="text-white">{t('contracts.fullNameForSignature')}</Label>
              <Input
                id="signerName"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder={t('contracts.enterFullName')}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
              />
            </div>

            {/* Signer Email - NEW */}
            <div className="space-y-2">
              <Label htmlFor="signerEmail" className="text-white">E-post</Label>
              <Input
                id="signerEmail"
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="din@epost.no"
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
              />
            </div>

            {/* Canvas Signature - Using react-signature-canvas */}
            <div className="space-y-2">
              <Label className="text-white">{t('contracts.drawSignature') || 'Tegn signatur'}</Label>
              <div className="relative border border-[#2a2a4a] rounded-lg bg-white">
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{
                    width: 500,
                    height: 150,
                    className: "w-full rounded-lg cursor-crosshair"
                  }}
                  penColor="#000"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('common.clear') || 'Slett'}
                </Button>
              </div>
              <p className="text-xs text-gray-500">{t('contracts.drawSignatureHint') || 'Bruk mus eller berøring for å tegne signaturen din'}</p>
            </div>

            {/* Payment Terms Selection */}
            <div className="space-y-3">
              <Label className="text-white">{t('contracts.paymentTermsChoice')}</Label>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentTerms(14)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentTerms === 14 
                      ? 'border-[#23F7DD] bg-[#23F7DD]/10' 
                      : 'border-[#2a2a4a] bg-[#0f0f1a] hover:border-[#23F7DD]/50'
                  }`}
                >
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${paymentTerms === 14 ? 'text-[#23F7DD]' : 'text-white'}`}>
                      14
                    </p>
                    <p className="text-sm text-gray-400">{t('contracts.daysPaymentTerms')}</p>
                    <p className="text-xs text-gray-500 mt-1">{t('contracts.standardOption')}</p>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => can30Days && setPaymentTerms(30)}
                  disabled={!can30Days}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    !can30Days 
                      ? 'border-yellow-500/50 bg-yellow-500/5 cursor-not-allowed'
                      : paymentTerms === 30 
                        ? 'border-[#23F7DD] bg-[#23F7DD]/10' 
                        : 'border-[#2a2a4a] bg-[#0f0f1a] hover:border-[#23F7DD]/50'
                  }`}
                >
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${!can30Days ? 'text-gray-600' : paymentTerms === 30 ? 'text-[#23F7DD]' : 'text-white'}`}>
                      30
                    </p>
                    <p className="text-sm text-gray-400">{t('contracts.daysPaymentTerms')}</p>
                    {can30Days ? (
                      <p className="text-xs text-gray-500 mt-1">{t('contracts.extendedOption') || 'Utvidet'}</p>
                    ) : (
                      <p className="text-sm font-semibold text-yellow-400 mt-2">
                        ⚠️ Kun for prosjekter over 250 000 kr
                      </p>
                    )}
                  </div>
                </button>
              </div>
              
              {/* Invoice explanation - under payment options */}
              <div className="bg-[#1e3a5f] border border-blue-400/30 rounded-lg p-4 mt-3">
                <p className="text-blue-200 text-sm">
                  <span className="font-semibold text-blue-100">📋 {t('contracts.invoiceInfoTitle') || 'Fakturainformasjon'}:</span><br/>
                  {t('contracts.invoiceFirstDay', { days: paymentTerms }) || `Fakturaen vil bli sendt første dag når vi starter arbeidet. Betalingsfristen på ${paymentTerms} dager regnes fra faktureringsdato.`}
                </p>
              </div>
            </div>

            {/* Sign Button */}
            <div className="flex gap-4 pt-4">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 border-[#2a2a4a] text-gray-400"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSign}
                disabled={signing || !agreedToTerms || !signerName.trim() || !signerEmail.trim()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('contracts.signingContract')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t('contracts.signContract')}
                  </>
                )}
              </Button>
            </div>

            <p className="text-sm font-semibold text-yellow-400 text-center bg-yellow-500/10 p-3 rounded-lg border border-yellow-500/30">
              ⚠️ {t('contracts.electronicSignature')}
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
